import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import InfinityMark from '../components/InfinityMark';
import BrandMark from '../components/BrandMark';
import LangSwitch from '../components/LangSwitch';
import { useT } from '../lib/i18n';

const HINT_STORAGE_KEY = 'ctl_lang_hint_seen';
const HINT_FADE_IN_MS = 1200;     // wait before the hint appears
const HINT_VISIBLE_MS = 6000;     // how long it lingers before fading away

export default function Entry({ onBegin }) {
  const { t } = useT();

  // First-visit-only language hint that gently points at the LangSwitch.
  // 0: not yet, 1: fading in, 2: visible, 3: fading out, 4: gone for good.
  const [hintPhase, setHintPhase] = useState(0);

  useEffect(() => {
    let seen = false;
    try { seen = localStorage.getItem(HINT_STORAGE_KEY) === '1'; } catch (_) {}
    if (seen) return;

    const t1 = setTimeout(() => setHintPhase(2), HINT_FADE_IN_MS);
    const t2 = setTimeout(() => setHintPhase(3), HINT_FADE_IN_MS + HINT_VISIBLE_MS);
    const t3 = setTimeout(() => {
      setHintPhase(4);
      try { localStorage.setItem(HINT_STORAGE_KEY, '1'); } catch (_) {}
    }, HINT_FADE_IN_MS + HINT_VISIBLE_MS + 800);

    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, []);

  return (
    <div className="min-h-screen flex flex-col fade-in font-pressura" data-testid="entry-screen">
      <header className="w-full px-4 sm:px-10 lg:px-16 pt-6 sm:pt-10 lg:pt-12 pb-6 sm:pb-8 grid grid-cols-3 items-center gap-2">
        <div className="justify-self-start flex items-center gap-2 sm:gap-6 min-w-0">
          <Link
            to="/history"
            className="text-[0.6rem] sm:text-xs tracking-[0.12em] sm:tracking-[0.18em] uppercase text-[#000000] hover:opacity-60 transition-opacity duration-300 whitespace-nowrap font-pressura"
            style={{ fontWeight: 700 }}
            data-testid="entry-history-link"
          >
            {t('nav.history')}
          </Link>
          <span className="hidden sm:inline text-[#000000] opacity-25 text-xs" aria-hidden="true">·</span>
          <Link
            to="/infinities"
            className="text-[0.6rem] sm:text-xs tracking-[0.12em] sm:tracking-[0.18em] uppercase text-[#000000] hover:opacity-60 transition-opacity duration-300 whitespace-nowrap font-pressura"
            style={{ fontWeight: 700 }}
            data-testid="entry-infinities-link"
          >
            {t('nav.infinitys')}
          </Link>
          <span className="hidden sm:inline text-[#000000] opacity-25 text-xs" aria-hidden="true">·</span>
          <Link
            to="/voimakortit"
            className="text-[0.6rem] sm:text-xs tracking-[0.12em] sm:tracking-[0.18em] uppercase text-[#000000] hover:opacity-60 transition-opacity duration-300 whitespace-nowrap font-pressura"
            style={{ fontWeight: 700 }}
            data-testid="entry-voimakortit-link"
          >
            {t('nav.voimakortit')}
          </Link>
        </div>
        <div className="justify-self-center" aria-hidden="true" />
        <div className="justify-self-end flex items-center gap-3 sm:gap-6">
          <LangSwitch testId="entry-lang-switch" />
          <span className="hidden sm:inline text-[#000000] opacity-25 text-xs" aria-hidden="true">·</span>
          <Link
            to="/settings"
            className="text-[0.6rem] sm:text-xs tracking-[0.12em] sm:tracking-[0.18em] uppercase text-[#000000] hover:opacity-60 transition-opacity duration-300 whitespace-nowrap font-pressura"
            style={{ fontWeight: 700 }}
            data-testid="entry-settings-link"
          >
            {t('nav.settings')}
          </Link>
        </div>
      </header>

      <main className="flex-1 flex flex-col items-center justify-center pt-2 pb-8 px-4">
        <Link to="/" className="block hover:opacity-90 transition-opacity duration-300" data-testid="entry-brand" aria-label="Connect to Love — home">
          <BrandMark size="hero" />
        </Link>

        <p
          className="font-pressura font-bold text-[#000000] text-center mt-12 sm:mt-16 max-w-3xl leading-snug"
          style={{ fontSize: 'clamp(1.0625rem, 2.2vw, 1.9375rem)' }}
          data-testid="entry-tagline"
        >
          {t('entry.tagline')}
        </p>

        <p
          className="font-pressura italic text-[#000000] text-center mt-5 sm:mt-7 max-w-2xl leading-relaxed opacity-70"
          style={{ fontSize: 'clamp(0.875rem, 1.4vw, 1.125rem)' }}
          data-testid="entry-renewal"
        >
          {t('entry.renewal')}
        </p>

        <div className="mt-10 sm:mt-14 flex justify-center" data-testid="entry-infinity-wrap">
          <InfinityMark size={120} stroke={5} className="sm:hidden" />
          <InfinityMark size={160} stroke={5} className="hidden sm:block" />
        </div>

        <button
          className="mt-10 sm:mt-12 ctl-shimmer-copper bg-transparent border-0 p-0 cursor-pointer font-pressura font-bold tracking-[0.18em] uppercase hover:opacity-80 transition-opacity duration-300"
          style={{ fontSize: 'clamp(1rem, 1.6vw, 1.25rem)', textDecoration: 'underline', textUnderlineOffset: '0.4em', textDecorationThickness: '2px' }}
          onClick={onBegin}
          data-testid="entry-begin-btn"
        >
          {t('entry.cta')}
        </button>
      </main>

      <footer className="ctl-container w-full pb-10 flex flex-col items-center justify-center gap-3" data-testid="entry-footer">
        {hintPhase > 0 && hintPhase < 4 && (
          <div
            className="text-[0.7rem] tracking-[0.06em] text-[#000000] transition-opacity duration-700 ease-out flex items-center gap-2 font-pressura"
            style={{ opacity: hintPhase === 2 ? 0.5 : 0 }}
            aria-hidden={hintPhase !== 2}
            data-testid="entry-lang-hint"
          >
            <span>{t('entry.langHint')}</span>
            <span aria-hidden="true">↑</span>
          </div>
        )}
      </footer>
    </div>
  );
}
